/** Every response shape the governance API returns. */
import type { ISODate } from "./dates";
import type { HubPriceTier, HubStrategicObjective } from "@otii/kit/hub-api";
import type { ApprovalDecision, ApprovalRequirement, AuthorityRule } from "./approvals";
import type { Basis, Direction, GateState, Repeat } from "./engine";

export type Role = "director" | "team" | "observer";
export const ROLE_LABEL: Record<Role, string> = { director: "Director", team: "Team", observer: "Observer" };

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  isAdmin: boolean;
  active: boolean;
  avatarUrl: string | null;
}

export interface Me {
  user: User;
  devLogin: boolean;
}

export interface AuthConfig {
  google: boolean;
  /** People on other domains can ask for an emailed sign-in link. */
  emailLink: boolean;
  devLogin: boolean;
  allowedDomain: string;
  devUsers: { id: string; name: string; email: string; role: string }[];
}

export interface ScheduleEntry {
  id: string;
  date: ISODate;
  repeat: Repeat | null;
  until: ISODate | null;
  /** Pence. */
  amount: number;
}

export interface Category {
  id: string;
  name: string;
  direction: Direction;
  sortOrder: number;
  active: boolean;
}

export interface LeverKind {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
}

export interface Commitment {
  id: string;
  name: string;
  direction: Direction;
  categoryId: string | null;
  basis: Basis;
  note: string;
  /** Can't be moved by a lever (payroll, PAYE, pension). */
  locked: boolean;
  /** The payment date is assumed, not known. */
  dateAssumed: boolean;
  /** Covered by this Xero contact: its bills (or invoices) replace the schedule up to the latest one. */
  xeroContactId: string | null;
  /** The supplier is connected to this director: anything touching the line is a related party, and they're stepped aside. */
  connectedDirectorId: string | null;
  active: boolean;
  sortOrder: number;
  schedule: ScheduleEntry[];
  updatedAt: string;
}

export type LeverStatus = "draft" | "proposed" | "approved" | "not_approved";
export const LEVER_STATUS_LABEL: Record<LeverStatus, string> = { draft: "Draft", proposed: "Proposed", approved: "Approved", not_approved: "Not approved" };

export interface LeverEntry extends ScheduleEntry {
  /** Pence, signed: + adds to cash, − takes from it. */
  amount: number;
  commitmentId: string | null;
  basis: Basis;
  note: string;
}

export interface Lever {
  id: string;
  title: string;
  kindId: string | null;
  narrative: string;
  status: LeverStatus;
  /** In the forecast. */
  applied: boolean;
  /** Connected to a director without being against one of their lines (a new supplier, say). */
  connectedDirectorId: string | null;
  entries: LeverEntry[];
  /** Its decision in the register, while proposed or once decided (the latest). */
  decisionId: string | null;
  decisionRef: number | null;
  /** Who must approve it, worked out when it was proposed (from its decision). */
  approval: ApprovalRequirement | null;
  /** Each approver's decision so far. */
  approvals: ApprovalDecision[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  proposedBy: string | null;
  proposedAt: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
}

export interface BankBalance {
  id: string;
  /** Entered by a person, or read from Xero's bank summary. */
  source: "manual" | "xero";
  asAt: ISODate;
  /** Pence. */
  amount: number;
  note: string;
  createdBy: string | null;
  createdAt: string;
}

export interface GateThresholds {
  id: string;
  effectiveFrom: ISODate;
  /** Pence. */
  green: number;
  amber: number;
  red: number;
  note: string;
  createdBy: string | null;
  createdAt: string;
  /** Lower than the version before in any state: reported to the next board. */
  loosened: boolean;
}

export interface FinanceSettings {
  /** 1–12: the month the financial year starts (7 = July, ending 30 June). */
  yearStartMonth: number;
  gateWeeks: number;
  runOutWeeks: number;
  /** How many weeks the cash chart shows (it marks where the gate's window ends). */
  chartWeeks: number;
  /** VAT added to the Sales Hub's prices (which are before VAT), as a percentage. */
  salesVatPct: number;
  /** Days between raising an invoice and being paid, for the Sales Hub's billing schedule. */
  paymentTermsDays: number;
  /** Complete months of Xero receipts the run-rate averages. */
  runRateMonths: number;
  /** Xero contacts left out of the run-rate (grants, VAT refunds, a one-off group deal). */
  runRateExcluded: string[];
  /** Xero bank accounts that make up the bank balance; empty for all of them. */
  bankAccountIds: string[];
}

/** Everything the finance screens calculate from. The engine runs in the browser on this, and on the server for the same answers. */
export interface FinanceModel {
  balance: BankBalance | null;
  balances: BankBalance[];
  thresholds: GateThresholds;
  settings: FinanceSettings;
  /** The year end for the balance date. */
  yearEnd: ISODate;
  categories: Category[];
  kinds: LeverKind[];
  commitments: Commitment[];
  levers: Lever[];
  people: Pick<User, "id" | "name" | "avatarUrl" | "role" | "active">[];
  /** Delegated authority, in order: the first rule that matches decides. */
  authority: AuthorityRule[];
  /** The decision type every lever takes, as the rules name it. */
  leverTypeId: string | null;
  /** What Xero and the Sales Hub last said. */
  synced: SyncedData;
}

/* ------------------------------------------------------------------ */
/* Xero and the Sales Hub                                              */
/* ------------------------------------------------------------------ */

export type SourceMode = "live" | "demo" | "off";

export interface SourceSync {
  at: string;
  ok: boolean;
  detail: string;
  counts: Record<string, number>;
}

export interface Connections {
  xero: {
    mode: SourceMode;
    /** Live: the Xero app is set up (client id and secret). */
    ready: boolean;
    connected: boolean;
    tenantName: string | null;
    /** Why it needs reconnecting, if it does. */
    error: string | null;
    sync: SourceSync | null;
  };
  hub: {
    mode: SourceMode;
    ready: boolean;
    sync: SourceSync | null;
  };
}

/** A bill Otii owes, or a sales invoice owed to Otii, as Xero has it. Pence, with VAT. */
export interface XeroDoc {
  id: string;
  kind: "bill" | "invoice";
  contactId: string;
  contactName: string;
  number: string;
  date: ISODate;
  dueDate: ISODate;
  total: number;
  amountDue: number;
  status: "AUTHORISED" | "PAID";
}

/** Money received: a payment against a sales invoice, or money received in the bank. Pence. */
export interface XeroReceipt {
  id: string;
  date: ISODate;
  contactId: string;
  contactName: string;
  amount: number;
}

/** One account's line in Xero's profit and loss for a month. Pence, as the report shows it (costs positive). */
export interface XeroPlLine {
  month: string;
  accountId: string;
  accountName: string;
  section: "income" | "cost_of_sales" | "expense" | "other_income" | "other";
  amount: number;
}

/** One bank account's month, from Xero's bank summary. Pence. */
export interface XeroBankMonth {
  month: string;
  accountId: string;
  accountName: string;
  opening: number;
  received: number;
  spent: number;
  closing: number;
}

/** Money spent straight from the bank (card, direct debit, standing order): not a bill. Pence, with VAT. */
export interface XeroSpend {
  id: string;
  date: ISODate;
  contactId: string;
  contactName: string;
  amount: number;
}

export interface XeroBankAccount {
  id: string;
  name: string;
  /** Pence: Xero's balance, as current as its bank reconciliation. */
  balance: number;
  asAt: ISODate;
}

/** One invoice a customer's contract says is due, from the Sales Hub. Pence, before VAT. */
export interface HubBillingLine {
  id: string;
  dealId: string;
  organisationId: string;
  organisationName: string;
  xeroContactIds: string[];
  date: ISODate;
  net: number;
  schedule: "annual" | "quarterly";
  sites: number;
  afterFreePeriod: boolean;
  discountPct: number | null;
  first: boolean;
}

export interface SyncedData {
  connections: Connections;
  xeroDocs: XeroDoc[];
  receipts: XeroReceipt[];
  bankAccounts: XeroBankAccount[];
  hubBilling: HubBillingLine[];
  objectives: HubStrategicObjective[];
  prices: HubPriceTier[];
}

export interface AuditEntry {
  id: string;
  at: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  summary: string;
}

export const GATE_LABEL: Record<GateState, string> = { green: "Green", amber: "Amber", red: "Red", stop: "Stop" };
export const REPEAT_LABEL: Record<Repeat | "once", string> = { once: "Once", week: "Weekly", month: "Monthly", quarter: "Quarterly", year: "Yearly" };

/* ------------------------------------------------------------------ */
/* Snapshots                                                           */
/* ------------------------------------------------------------------ */

/** A lever as it stood when a snapshot was taken. */
export interface SnapshotLever {
  id: string;
  title: string;
  kind: string | null;
  status: LeverStatus;
  narrative: string;
  entries: LeverEntry[];
  /** Net cash it moved to the year end (pence). */
  net: number;
}

/** A commitment as it stood when a snapshot was taken. */
export interface SnapshotLine {
  id: string;
  name: string;
  direction: Direction;
  category: string | null;
  basis: Basis;
  locked: boolean;
  dateAssumed: boolean;
  schedule: ScheduleEntry[];
}

/** What a list of snapshots shows. */
export interface SnapshotSummary {
  id: string;
  name: string;
  note: string;
  /** The view it was taken in: every applied lever, or only the approved ones. */
  view: "applied" | "approved";
  takenBy: string | null;
  takenByName: string | null;
  takenAt: string;
  /** The bank balance it started from. */
  asOf: ISODate;
  opening: number;
  gate: GateState;
  low: { start: ISODate; close: number };
  firstOverdrawn: ISODate | null;
  yearEnd: ISODate;
  yearEndCash: number;
  leverCount: number;
}

/** A frozen record of the forecast: what it was worked out from, and what it showed. */
export interface Snapshot extends SnapshotSummary {
  balanceNote: string;
  thresholds: Pick<GateThresholds, "green" | "amber" | "red" | "effectiveFrom">;
  settings: FinanceSettings;
  levers: SnapshotLever[];
  lines: SnapshotLine[];
  input: import("./engine").ForecastInput;
  forecast: import("./engine").Forecast;
  /** To stay above the stop line, as the snapshot stood. */
  salesNeeded: import("./engine").SalesNeeded;
}

/** What re-applying a snapshot's levers did. */
export interface ReapplyResult {
  applied: string[];
  takenOut: string[];
  /** In the snapshot but deleted since. */
  missing: string[];
  /** Marked not approved since, so not applied. */
  notApproved: string[];
  /** Applied, but its amounts or dates have changed since the snapshot. */
  changed: string[];
}
